// Org SSO admin UI-state controller (#468). Owns the save/remove/notice-flash
// state + handlers so `routes/AccountSso` stays a thin presenter under the
// 150-LOC limit. Data layer (query + mutations) lives in `./orgSso`; this is
// the interaction flow, so it changes for a different reason (SRP).

import { createSignal } from 'solid-js';
import { t } from '../i18n';
import { ApiError } from './api';
import { joinSentences } from './join-sentences';
import { type OrgSsoPutBody, putOrgSso, removeOrgSso, useOrgSso } from './orgSso';
import { createToast, NOTICE_TTL_MS } from './toast';

/** The account SSO screen's save / remove / notice state and handlers, over `useOrgSso`'s data. */
export function useOrgSsoAdmin(token: string | null, enabled: () => boolean = () => true) {
  const query = useOrgSso(token, enabled);

  const [busy, setBusy] = createSignal(false);
  const [saveError, setSaveError] = createSignal<string | null>(null);
  const [confirmOpen, setConfirmOpen] = createSignal(false);
  const [removeError, setRemoveError] = createSignal<string | null>(null);

  // "Saved" flash on the container so it survives the post-save refetch, which
  // remounts the summary + form subtree. Auto-dismisses (mirrors EmailRoutes).
  const flashed = createToast(NOTICE_TTL_MS);
  const notice = (): string | null => flashed.toast()?.message ?? null;
  const flash = (msg: string): void => flashed.notify(msg);

  // Toast dismissal (X on the flash / a lingering remove-error) — the route's
  // single Toast surface renders whichever of the two is set.
  const dismissNotice = (): void => {
    flashed.dismiss();
    setRemoveError(null);
  };

  const save = async (body: OrgSsoPutBody): Promise<void> => {
    setBusy(true);
    setSaveError(null);
    try {
      await putOrgSso(body, token);
      // Refetch only on success — a failed save keeps the user's edits mounted.
      await query.refetch();
      flash(t('account_sso.saved'));
    } catch (e) {
      // 422 = live issuer discovery rejected the config; surface its reason.
      setSaveError(
        e instanceof ApiError && e.status === 422
          ? joinSentences(
              [t('account_sso.save_error_invalid'), e.detail].filter((s): s is string =>
                Boolean(s),
              ),
            )
          : t('account_sso.save_error'),
      );
    } finally {
      setBusy(false);
    }
  };

  const askRemove = (): void => {
    setRemoveError(null);
    setConfirmOpen(true);
  };

  const confirmRemove = async (): Promise<void> => {
    setBusy(true);
    setRemoveError(null);
    try {
      await removeOrgSso(token);
      setConfirmOpen(false);
      await query.refetch();
    } catch {
      // Surface it + keep the modal open — a destructive action must never
      // silently no-op (same stance as AccountKeys' revoke).
      setRemoveError(t('account_sso.remove_error'));
    } finally {
      setBusy(false);
    }
  };

  return {
    query,
    busy,
    saveError,
    notice,
    confirmOpen,
    setConfirmOpen,
    removeError,
    save,
    askRemove,
    confirmRemove,
    dismissNotice,
  };
}
